import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Activity, Download, AlertTriangle } from 'lucide-react';
import SimulationSidebar, { SidebarConfig } from './SimulationSidebar';
import DownloadSection from './DownloadSection';
import AlertsSection from './AlertsSection';
import RtcSimulationSection from './RtcSimulationSection';
import TokenOperationsSection from './TokenOperationsSection';
import ToastNotification from './ToastNotification';
import { ExecutionStatus, CustomDates, DownloadHistory, ToastState, SimulationInputConfig } from './types';
import { useRoleBasedUserFilter } from './useRoleBasedUserFilter';
import '../../../styles/np-theme.css';

const _normalizeApiBase = (value: string): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  if (raw.startsWith(':')) {
    return `${window.location.protocol}//${window.location.hostname}${raw}`;
  }
  if (raw.startsWith('//')) {
    return `${window.location.protocol}${raw}`;
  }
  if (raw.startsWith('/')) {
    return `${window.location.origin}${raw}`;
  }

  return raw.replace(/\/+$/, '');
};

// API Configuration
const API_BASE =
  _normalizeApiBase((import.meta as any).env?.VITE_API_BASE || '') ||
  _normalizeApiBase((window as any).__API_BASE__ || '') ||
  window.location.origin;

const STATUS_CHECK_TIMEOUT = 5000;

const isTokenExpired = (token: string): boolean => {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return true;
    const payload = JSON.parse(atob(parts[1]));
    const exp = Number(payload?.exp || 0);
    if (!exp) return false;
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
};

const getStoredToken = (): string => {
  return String(
    sessionStorage.getItem('access_token') ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('token') ||
    localStorage.getItem('auth_token') ||
    sessionStorage.getItem('auth_token') || ''
  ).trim();
};

const hasUsableAuthSession = (): boolean => {
  const token = getStoredToken();
  if (!token) return false;
  return !isTokenExpired(token);
};

// Utility Functions
const withTimeout = (promise: Promise<Response>, timeoutMs: number = STATUS_CHECK_TIMEOUT): Promise<Response> => {
  return Promise.race([
    promise.catch((error) => {
      // Re-throw to be handled by safeFetchJson
      throw error;
    }),
    new Promise<Response>((_, reject) =>
      setTimeout(() => reject(new Error('Status check timeout')), timeoutMs)
    )
  ]);
};

const safeFetchJson = async (url: string, options?: RequestInit): Promise<any> => {
  try {
    const response = await withTimeout(fetch(url, options).catch(() => {
      // Catch fetch errors immediately to prevent unhandled promise rejections
      throw new Error('CONNECTION_ERROR');
    }));
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return await response.json();
  } catch (error: any) {
    // Suppress connection refused and timeout errors - they're expected when server is down
    const errorMessage = error?.message || '';
    const isConnectionError = errorMessage === 'CONNECTION_ERROR' ||
      errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('ERR_CONNECTION_REFUSED') ||
      errorMessage.includes('CONNECTION_REFUSED') ||
      errorMessage.includes('timeout') ||
      errorMessage.includes('Status check timeout') ||
      error?.name === 'TypeError';

    // Don't log or throw connection errors - just return null to indicate server is unavailable
    if (isConnectionError) {
      return null;
    }

    // Only log non-connection errors
    console.error('Fetch error:', error);
    throw error;
  }
};

const safeFetchBlob = async (url: string, options?: RequestInit, timeoutMs: number = 60000): Promise<{ blob: Blob; response: Response } | { status: number; response: Response } | null> => {
  try {
    const response = await withTimeout(fetch(url, options), timeoutMs);
    // Handle 304 Not Modified as a special case (success but no blob)
    if (response.status === 304) {
      return { status: 304, response };
    }
    if (!response.ok) {
      // Return special status codes for caller to handle
      if (response.status === 501 || response.status === 404) {
        return { status: response.status, response };
      }
      const errorText = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status} ${response.statusText}: ${errorText}`);
    }
    const blob = await response.blob();
    return { blob, response };
  } catch (error: any) {
    // Suppress connection refused and timeout errors
    const errorMessage = error?.message || '';
    const isConnectionError = errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('ERR_CONNECTION_REFUSED') ||
      errorMessage.includes('CONNECTION_REFUSED') ||
      errorMessage.includes('timeout') ||
      errorMessage.includes('Status check timeout') ||
      error?.name === 'TypeError';

    // Return null for connection errors (server unavailable)
    if (isConnectionError) {
      return null;
    }

    // Re-throw other errors
    throw error;
  }
};

const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, 100);
};

const getFilenameFromResponse = (response: Response, fallback: string): string => {
  const contentDisposition = response.headers.get('Content-Disposition') || response.headers.get('content-disposition');
  if (!contentDisposition) return fallback;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const basicMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return basicMatch?.[1] || fallback;
};

const getAuthAndTenantHeaders = (siteCode?: string): Record<string, string> => {
  const token = getStoredToken();
  const effectiveSiteCode = siteCode || sessionStorage.getItem('tenant') || localStorage.getItem('currentSite') || '';
  const headers: Record<string, string> = {};

  if (token) headers.Authorization = `Bearer ${token}`;
  if (effectiveSiteCode) headers['X-Site-Code'] = String(effectiveSiteCode);

  return headers;
};

const buildHistoryStorageKey = (baseKey: 'downloadHistory' | 'alertHistory', siteCode?: string): string => {
  const userId = sessionStorage.getItem('user_id') || 'anonymous';
  const companyId = sessionStorage.getItem('company_id') || localStorage.getItem('company_id') || 'none';
  const scopedSite = siteCode || sessionStorage.getItem('tenant') || localStorage.getItem('currentSite') || 'unknown';
  return `${baseKey}:${companyId}:${userId}:${String(scopedSite).toUpperCase()}`;
};

// Helper functions to safely get from localStorage
const getDownloadHistoryFromStorage = (siteCode?: string): DownloadHistory[] => {
  try {
    const saved = localStorage.getItem(buildHistoryStorageKey('downloadHistory', siteCode));
    if (saved) {
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch (error) {
    console.error('Error loading download history:', error);
  }
  return [];
};

const getAlertHistoryFromStorage = (siteCode?: string): DownloadHistory[] => {
  try {
    const saved = localStorage.getItem(buildHistoryStorageKey('alertHistory', siteCode));
    if (saved) {
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch (error) {
    console.error('Error loading alert history:', error);
  }
  return [];
};

const SimulationOnDemand: React.FC = () => {
  const { site } = useParams();
  const location = useLocation();
  const siteName = useMemo(() => site?.toUpperCase() || 'UNKNOWN', [site]);

  const parseSectionFromState = useCallback((state: any): 'wallet' | 'download' | 'alerts' | 'rtc' => {
    const section = state?.simulationSection;
    if (section === 'download' || section === 'alerts' || section === 'rtc' || section === 'wallet') {
      try {
        localStorage.setItem('simulation_active_section', section);
      } catch (e) {}
      return section;
    }
    try {
      const stored = localStorage.getItem('simulation_active_section');
      if (stored === 'download' || stored === 'alerts' || stored === 'rtc' || stored === 'wallet') {
        return stored;
      }
    } catch (e) {}
    return 'wallet';
  }, []);

  // State Management
  const [activeSection, setActiveSection] = useState<'wallet' | 'download' | 'alerts' | 'rtc'>(() => parseSectionFromState(location.state));

  useEffect(() => {
    try {
      localStorage.setItem('simulation_active_section', activeSection);
    } catch (e) {}
  }, [activeSection]);
  // Role-based user filter
  const {
    accessibleUsers,
    selectedUserId,
    setSelectedUserId,
    viewerRole,
    isLoading: isRoleFilterLoading,
  } = useRoleBasedUserFilter(siteName);

  const fallbackViewerRole = String(sessionStorage.getItem('role_name') || sessionStorage.getItem('user_role') || '').trim().toLowerCase().replace(' ', '_');
  const [walletViewerRole, setWalletViewerRole] = useState<string>('');
  const [walletCanExecuteTabs, setWalletCanExecuteTabs] = useState<boolean>(false);
  const [tokenBalance, setTokenBalance] = useState<number | null>(null);
  const [tokenPricing, setTokenPricing] = useState<Record<string, { label: string; cost: number }>>({});
  const [tokenWalletLoading, setTokenWalletLoading] = useState<boolean>(true);
  const [tokenRequests, setTokenRequests] = useState<Array<{
    id: string;
    requestedByUserId: number;
    requestedByName: string;
    requestedByEmail?: string;
    requestedTokens: number;
    remarks: string;
    status: string;
    createdAt: string;
  }>>([]);
  const [tokenRequestsLoading, setTokenRequestsLoading] = useState<boolean>(false);
  const [requestAmount, setRequestAmount] = useState<string>('');
  const [requestRemarks, setRequestRemarks] = useState<string>('');
  const [approveInProgressId, setApproveInProgressId] = useState<string | null>(null);

  const effectiveViewerRole = String(walletViewerRole || viewerRole || fallbackViewerRole || 'general_user').toLowerCase();
  const canAccessSimulationPage = effectiveViewerRole === 'ho_admin' || effectiveViewerRole === 'system_admin';
  const canExecuteSimulationTabs = effectiveViewerRole === 'ho_admin' || effectiveViewerRole === 'system_admin' || walletCanExecuteTabs;
  const canViewActivityFilterSection = effectiveViewerRole === 'system_admin';
  const [downloadStatus, setDownloadStatus] = useState<ExecutionStatus>({ status: 'idle' });
  const [alertStatus, setAlertStatus] = useState<ExecutionStatus>({ status: 'idle' });
  const [rtcStatus, setRtcStatus] = useState<ExecutionStatus>({ status: 'idle' });
  const [customDates, setCustomDates] = useState<CustomDates>({
    startDate: '',
    endDate: '',
    singleDate: ''
  });
  const [dateRangeMode, setDateRangeMode] = useState<'single' | 'range'>('single');
  const [simulationInputs, setSimulationInputs] = useState<SimulationInputConfig>({
    localInputPath: '',
    outputPaths: '',
    azureSasKey: '',
    cwd: ''
  });
  const [alertsEtag, setAlertsEtag] = useState<string | null>(null);
  const [pollingKey, setPollingKey] = useState(0); // Key to restart polling

  // Initialize Download History State with localStorage data
  const [downloadHistory, setDownloadHistory] = useState<DownloadHistory[]>(() => {
    return getDownloadHistoryFromStorage(site);
  });

  // Initialize Alert History State with localStorage data
  const [alertHistory, setAlertHistory] = useState<DownloadHistory[]>(() => {
    return getAlertHistoryFromStorage(site);
  });

  // Toast State
  const [toast, setToast] = useState<ToastState>({
    show: false,
    type: 'success',
    title: '',
    message: ''
  });

  // Sidebar collapsed state - track to adjust main content margin
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('simulationSidebarCollapsed');
      return stored === 'true';
    } catch (e) {
}
    return false;
  });

  // Calculate sidebar width based on collapsed state
  const sidebarWidth = sidebarCollapsed ? 64 : 280;

  // Listen for sidebar collapse state changes from localStorage
  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const stored = localStorage.getItem('simulationSidebarCollapsed');
        setSidebarCollapsed(stored === 'true');
      } catch (e) {
}
    };

    // Poll localStorage every 100ms to detect changes
    const interval = setInterval(handleStorageChange, 100);
    return () => clearInterval(interval);
  }, []);


  // Save download history to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(buildHistoryStorageKey('downloadHistory', site), JSON.stringify(downloadHistory));
    } catch (error) {
      console.error('Error saving download history:', error);
    }
  }, [downloadHistory, site]);

  // Save alert history to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(buildHistoryStorageKey('alertHistory', site), JSON.stringify(alertHistory));
    } catch (error) {
      console.error('Error saving alert history:', error);
    }
  }, [alertHistory, site]);

  // Keep section in sync with sidebar navigation state
  useEffect(() => {
    const nextSection = parseSectionFromState(location.state);
    setActiveSection((current) => (current === nextSection ? current : nextSection));
  }, [location.state, parseSectionFromState]);

  const sidebarConfig: Partial<SidebarConfig> = useMemo(() => ({
    // Customize header
    header: {
      title: 'Simulation-On-Demand',
      subtitle: 'Data Operations',
      icon: Activity,
      iconColor: '#0084CF'
    },
    // Customize menu items
    menuItems: [
      {
        id: 'wallet',
        icon: Activity,
        label: 'Simulation-On-Demand',
        description: 'Data Operations',
        color: '#0084CF',
        status: { status: 'idle' }
      },
      {
        id: 'download',
        icon: Download,
        label: 'Download Raw Data',
        description: 'To local',
        color: '#0084CF',
        status: downloadStatus
      },
      {
        id: 'alerts',
        icon: AlertTriangle,
        label: 'Generate Alerts',
        description: 'SCB, Inverters, etc',
        color: '#0084CF',
        status: alertStatus
      },
      {
        id: 'rtc',
        imageUrl: '/rtc-optimization-logo.png',
        label: 'Run RTC Optimization',
        description: 'Automatic and Manual',
        color: '#0084CF',
        status: rtcStatus
      }
    ],
    // Show/hide status footer
    showStatusFooter: true,
    statusFooterText: 'System Status'
  }), [downloadStatus, alertStatus, rtcStatus]);

  const fetchTokenWallet = useCallback(async () => {
    setTokenWalletLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/fulfillment/token-wallet?site_code=${encodeURIComponent(siteName)}`, {
        headers: getAuthAndTenantHeaders(siteName)
      });

      if (!response.ok) {
        setTokenBalance(null);
        setTokenPricing({});
        return;
      }

      const payload = await response.json();
      setWalletViewerRole(String(payload?.viewer_role || '').toLowerCase());
      setWalletCanExecuteTabs(!!payload?.can_execute_tabs);
      const isSystemAdmin = String(payload?.viewer_role || '').toLowerCase() === 'system_admin';
      setTokenBalance(
        isSystemAdmin || payload?.wallet?.token_balance === null
          ? null
          : (typeof payload?.wallet?.token_balance === 'number' ? payload.wallet.token_balance : 0)
      );
      setTokenPricing(payload?.pricing || {});
    } catch {
      setWalletCanExecuteTabs(false);
      setTokenBalance(null);
      setTokenPricing({});
    } finally {
      setTokenWalletLoading(false);
    }
  }, [siteName]);

  const fetchTokenRequests = useCallback(async () => {
    setTokenRequestsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/fulfillment/token-requests?site_code=${encodeURIComponent(siteName)}`, {
        headers: getAuthAndTenantHeaders(siteName)
      });

      if (!response.ok) {
        setTokenRequests([]);
        return;
      }

      const payload = await response.json();
      const requests = Array.isArray(payload?.requests) ? payload.requests : [];
      setTokenRequests(requests.map((item: any) => ({
        id: String(item.id),
        requestedByUserId: Number(item.requested_by_user_id),
        requestedByName: String(item.requested_by_name || item.requested_by_username || 'User'),
        requestedByEmail: String(item.requested_by_email || ''),
        requestedTokens: Number(item.requested_tokens || 0),
        remarks: String(item.remarks || ''),
        status: String(item.status || 'Pending'),
        createdAt: String(item.created_at || ''),
      })));
    } catch {
      setTokenRequests([]);
    } finally {
      setTokenRequestsLoading(false);
    }
  }, [siteName]);

  const submitTokenRequest = useCallback(async (tab?: string) => {
    const amount = Number(requestAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setToast({
        show: true,
        type: 'error',
        title: 'Invalid Tokens',
        message: 'Please enter a valid token amount greater than 0.'
      });
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/v1/fulfillment/token-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthAndTenantHeaders(siteName),
        },
        body: JSON.stringify({
          requested_tokens: amount,
          remarks: requestRemarks,
          tab: tab || undefined,
          site_code: siteName,
        })
      });

      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body?.message || 'Unable to submit token request.');
      }

      setToast({
        show: true,
        type: 'success',
        title: 'Request Submitted',
        message: 'Recharge request sent to System Admin.'
      });
      setRequestAmount('');
      setRequestRemarks('');
      await fetchTokenRequests();
    } catch (error: any) {
      setToast({
        show: true,
        type: 'error',
        title: 'Request Failed',
        message: error?.message || 'Unable to submit token request.'
      });
    }
  }, [requestAmount, requestRemarks, siteName, fetchTokenRequests]);

  const approveTokenRequest = useCallback(async (requestId: string, requestedByUserId: number, tokenAmount: number) => {
    setApproveInProgressId(requestId);
    try {
      const response = await fetch(`${API_BASE}/api/v1/fulfillment/token-requests/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthAndTenantHeaders(siteName),
        },
        body: JSON.stringify({
          request_id: requestId,
          user_id: requestedByUserId,
          token_amount: tokenAmount,
        })
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body?.message || 'Unable to approve request.');
      }

      setToast({
        show: true,
        type: 'success',
        title: 'Request Approved',
        message: `Assigned ${tokenAmount} tokens successfully.`
      });

      await fetchTokenWallet();
      await fetchTokenRequests();
    } catch (error: any) {
      setToast({
        show: true,
        type: 'error',
        title: 'Approval Failed',
        message: error?.message || 'Unable to approve request.'
      });
    } finally {
      setApproveInProgressId(null);
    }
  }, [siteName, fetchTokenWallet, fetchTokenRequests]);

  useEffect(() => {
    if (!isRoleFilterLoading) {
      fetchTokenWallet();
      if (canAccessSimulationPage) {
        fetchTokenRequests();
      } else {
        setTokenRequests([]);
      }
      return;
    }
  }, [isRoleFilterLoading, canAccessSimulationPage, fetchTokenWallet, fetchTokenRequests]);

  const fetchHistory = useCallback(async () => {
    let nextDownloadHistory: DownloadHistory[] = [];
    let nextAlertHistory: DownloadHistory[] = [];
    try {
      const userParam = selectedUserId ? `&view_user_id=${selectedUserId}` : '';
      const response = await fetch(
        `${API_BASE}/api/v1/fulfillment/simulation-history?site_code=${site}${userParam}`,
        { headers: getAuthAndTenantHeaders(site) }
      );
      if (response.ok) {
        const data = await response.json();
        const mappedHistory: Array<DownloadHistory & { _isAlert: boolean }> = data.map((item: any) => {
          const activityType = String(item.activity_type || '').toLowerCase();
          const message = String(item.message || '').toLowerCase();
          const isAlert = activityType.includes('alert') || message.includes('alert');
          const isWebScrape =
            activityType.includes('web-scrape') ||
            activityType.includes('web_scrape') ||
            message.includes('web scrape') ||
            message.includes('web-scrape');
          let dateDisplay = '';
          if (item.from_date && item.to_date) {
            dateDisplay = item.from_date === item.to_date ? item.from_date : `${item.from_date} to ${item.to_date}`;
          } else {
            dateDisplay = new Date(item.created_at).toLocaleDateString();
          }

          return {
            id: String(item.activity_id),
            filename: item.message || 'Simulation Record',
            activityType: item.activity_type || '',
            source: isWebScrape ? 'web_scrape' : 'azure',
            fromDate: item.from_date || '',
            toDate: item.to_date || '',
            dateDisplay,
            size: item.total_size || '0 B',
            fileCount: item.total_files || 0,
            status: item.status === 'Completed' ? 'completed' : 'failed',
            path: item.working_directory || '',
            timestamp: item.created_at,
            siteId: String(item.site_id || ''),
            _isAlert: isAlert,
          };
        });
        nextDownloadHistory = mappedHistory.filter((item) => !item._isAlert).map(({ _isAlert, ...rest }) => rest);
        nextAlertHistory = mappedHistory.filter((item) => item._isAlert).map(({ _isAlert, ...rest }) => rest);
        setDownloadHistory(nextDownloadHistory);
        setAlertHistory(nextAlertHistory);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
    return { downloadHistory: nextDownloadHistory, alertHistory: nextAlertHistory };
  }, [site, selectedUserId]);

  // Reload history when site or selected view-user context changes.
  useEffect(() => {
    setDownloadHistory(getDownloadHistoryFromStorage(site));
    setAlertHistory(getAlertHistoryFromStorage(site));
    fetchHistory();
  }, [site, fetchHistory, selectedUserId]);

  // Download generated Web Scraped file from backend
  const downloadWebScrapeFile = useCallback(async (activityId: string, filename: string) => {
    try {
      setDownloadStatus(prev => ({
        ...prev,
        message: 'Downloading zip archive...',
        progress: 98
      }));
      const endpoint = `${API_BASE}/api/v1/fulfillment/web-scrape-download/${activityId}`;
      const response = await fetch(endpoint, {
        headers: getAuthAndTenantHeaders(site)
      });
      if (!response.ok) {
        throw new Error(`Failed to download file (${response.status})`);
      }
      const blob = await response.blob();
      saveBlob(blob, filename);
      
      setToast({
        show: true,
        type: 'success',
        title: 'Download Complete',
        message: `${filename} downloaded successfully.`
      });
    } catch (err: any) {
      console.error('Failed to download file after completion:', err);
      setToast({
        show: true,
        type: 'error',
        title: 'Download Failed',
        message: err.message || 'Failed to download the generated archive.'
      });
    } finally {
      // Clear execution status and refresh history
      setDownloadStatus({ status: 'idle' });
      fetchHistory();
    }
  }, [site, fetchHistory]);

  // Stop/cancel a running Web Scraper process
  const handleStopExecution = useCallback(async () => {
    const executionId = downloadStatus.executionId || downloadStatus.run_id;
    if (!executionId) {
      setDownloadStatus({ status: 'idle' });
      return;
    }

    try {
      setToast({
        show: true,
        type: 'info',
        title: 'Stopping Scraper',
        message: 'Stopping the web scraping process...'
      });

      // Clear local status immediately for quick UI response
      setDownloadStatus({ status: 'idle' });

      const response = await fetch(`${API_BASE}/api/v1/fulfillment/stop-web-scrape`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthAndTenantHeaders(site)
        },
        body: JSON.stringify({ execution_id: executionId })
      });

      if (!response.ok) {
        throw new Error('Failed to stop the scraper on the server');
      }

      setToast({
        show: true,
        type: 'success',
        title: 'Scraper Stopped',
        message: 'Web scraping has been stopped successfully.'
      });
      fetchHistory();
    } catch (err: any) {
      console.error('Error stopping web scraper:', err);
      setToast({
        show: true,
        type: 'error',
        title: 'Stop Failed',
        message: err.message || 'Could not stop the scraper'
      });
    }
  }, [site, downloadStatus.executionId, downloadStatus.run_id, fetchHistory]);

  // Status checking functions
  const checkDownloadStatus = useCallback(async () => {
    if (!hasUsableAuthSession()) return 'unauthorized';

    try {
      const status = await safeFetchJson(`${API_BASE}/api/execution-status`, {
        headers: getAuthAndTenantHeaders(site)
      });
      if (status === null) {
        // Server unavailable - reset to idle to clear any failed/running states
        setDownloadStatus(prev => {
          // Only update if status is not already idle (avoid unnecessary re-renders)
          if (prev.status !== 'idle') {
            return { status: 'idle' };
          }
          return prev;
        });
        return false; // Return false to indicate server is down
      }
      // Update status with server response
      setDownloadStatus((prev) => {
        // If it was running a web scrape and the new status is completed
        if (prev?.status === 'running' && prev?.isWebScrape && status?.status === 'completed') {
          // Trigger file download asynchronously
          const actId = status.activity_id || prev.activityId;
          const fname = status.filename || `scraped_data_${actId}.zip`;
          setTimeout(() => downloadWebScrapeFile(actId, fname), 0);
          return status;
        }

        // Avoid regressing UI from completed -> running when client-side finalization has already finished.
        if (prev?.status === 'completed' && status?.status === 'running') {
          return prev;
        }

        // Preserve properties for running state
        if (status?.status === 'running') {
          return {
            ...status,
            isWebScrape: prev?.isWebScrape,
            executionId: prev?.executionId,
            activityId: prev?.activityId
          };
        }

        return status;
      });
      return status;
    } catch (error: any) {
      if (error?.message?.includes('401')) return 'unauthorized';
      throw error;
    }
  }, [site, downloadWebScrapeFile]);

  const checkAlertStatus = useCallback(async () => {
    if (!hasUsableAuthSession()) return 'unauthorized';

    try {
      const status = await safeFetchJson(`${API_BASE}/api/alert-status`, {
        headers: getAuthAndTenantHeaders(site)
      });
      if (status === null) {
        // Server unavailable - reset to idle to clear any failed/running states
        setAlertStatus(prev => {
          // Only update if status is not already idle (avoid unnecessary re-renders)
          if (prev.status !== 'idle') {
            return { status: 'idle' };
          }
          return prev;
        });
        return false; // Return false to indicate server is down
      }
      // Update status with server response
      setAlertStatus(status);
      return status;
    } catch (error: any) {
      if (error?.message?.includes('401')) return 'unauthorized';
      throw error;
    }
  }, [site]);

  const updateSimulationInput = useCallback((key: keyof SimulationInputConfig, value: string) => {
    setSimulationInputs(prev => ({ ...prev, [key]: value }));
  }, []);

  const appendOutputPath = useCallback((pathValue: string) => {
    const cleaned = pathValue.trim();
    if (!cleaned) return;
    setSimulationInputs(prev => {
      const existing = prev.outputPaths
        .split(',')
        .map(path => path.trim())
        .filter(Boolean);
      if (existing.includes(cleaned)) {
        return prev;
      }
      const merged = [...existing, cleaned].join(', ');
      return { ...prev, outputPaths: merged };
    });
  }, []);

  const browseFolder = useCallback(async () => {
    try {
      const pickerApi = (window as any).showDirectoryPicker;
      if (typeof pickerApi === 'function') {
        const handle = await pickerApi();
        const selectedName = String(handle?.name || '').trim();
        return selectedName || null;
      }

      const input = document.createElement('input') as HTMLInputElement & {
        webkitdirectory?: boolean;
        directory?: boolean;
      };
      input.type = 'file';
      input.multiple = true;
      input.webkitdirectory = true;
      input.directory = true;

      return new Promise<string | null>((resolve) => {
        input.onchange = () => {
          const files = input.files;
          if (!files || files.length === 0) {
            resolve(null);
            return;
          }
          const first = files[0] as File & { webkitRelativePath?: string };
          const relative = String(first.webkitRelativePath || '');
          const folderName = relative.split('/')[0] || '';
          resolve(folderName || null);
        };
        input.click();
      });
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return null;
      }
      setToast({
        show: true,
        type: 'error',
        title: 'Folder Selection Failed',
        message: error?.message || 'Unable to open folder picker in this browser.'
      });
      return null;
    }
  }, []);

  const browseLocalInputFolder = useCallback(async () => {
    const folder = await browseFolder();
    if (folder) {
      updateSimulationInput('localInputPath', folder);
    }
  }, [browseFolder, updateSimulationInput]);

  const browseOutputFolder = useCallback(async () => {
    const folder = await browseFolder();
    if (folder) {
      appendOutputPath(folder);
    }
  }, [browseFolder, appendOutputPath]);

  const browseCwdFolder = useCallback(async () => {
    const folder = await browseFolder();
    if (folder) {
      updateSimulationInput('cwd', folder);
    }
  }, [browseFolder, updateSimulationInput]);

  // Redownload function for history items
  const handleRedownload = async (item: DownloadHistory) => {
    if (!canExecuteSimulationTabs) {
      setToast({
        show: true,
        type: 'error',
        title: 'Execution Not Allowed',
        message: 'Only HO Admin can execute simulation tabs.'
      });
      return;
    }

    try {
      if (activeSection === 'download') {
        setDownloadStatus({
          status: 'running',
          started_at: new Date().toISOString(),
          message: 'Re-downloading file...'
        });
      } else {
        setAlertStatus({
          status: 'running',
          started_at: new Date().toISOString(),
          message: 'Re-downloading file...'
        });
      }

      const isWS =
        item.source === 'web_scrape' ||
        String(item.activityType || '').toLowerCase().includes('web-scrape') ||
        String(item.activityType || '').toLowerCase().includes('web_scrape');

      let endpoint = '';
      let fetchOptions = {};

      if (isWS) {
        endpoint = `${API_BASE}/api/v1/fulfillment/web-scrape-download/${item.id}`;
        fetchOptions = {
          headers: {
            ...getAuthAndTenantHeaders(site)
          }
        };
      } else {
        const body = {
          site_id: item.siteId,
          from_date: item.fromDate,
          to_date: item.toDate
        };
        endpoint = activeSection === 'download'
          ? `${API_BASE}/api/v1/fulfillment/raw-zip`
          : `${API_BASE}/api/v1/fulfillment/alerts-csv?date=${encodeURIComponent(item.fromDate)}`;
        fetchOptions = activeSection === 'download'
          ? {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthAndTenantHeaders(site)
            },
            body: JSON.stringify(body)
          }
          : {
            headers: {
              ...getAuthAndTenantHeaders(site)
            }
          };
      }

      const result = await safeFetchBlob(endpoint, fetchOptions);

      if (result === null) {
        // Server unavailable
        if (activeSection === 'download') {
          setDownloadStatus({
            status: 'idle',
            message: 'Server unavailable'
          });
        } else {
          setAlertStatus({
            status: 'idle',
            message: 'Server unavailable'
          });
        }
        setToast({
          show: true,
          type: 'error',
          title: 'Server Unavailable',
          message: 'Cannot connect to the server. Please ensure the backend server is running and API base URL is correct.'
        });
        return;
      }

      // Handle special status codes
      if ('status' in result) {
        throw new Error(`HTTP ${result.status}: ${result.response.statusText}`);
      }

      const fallbackName = item.filename || `simulation-${item.fromDate}.txt`;
      const downloadedFilename = getFilenameFromResponse(result.response, fallbackName);
      const tokenRemainingHeader = result.response.headers.get('X-Tokens-Remaining');
      if (tokenRemainingHeader && !Number.isNaN(Number(tokenRemainingHeader))) {
        setTokenBalance(Number(tokenRemainingHeader));
      }

      saveBlob(result.blob, downloadedFilename);

      const timestamp = new Date().toISOString();
      if (activeSection === 'download') {
        setDownloadStatus({
          status: 'completed',
          completed_at: timestamp,
          message: `Re-downloaded ${downloadedFilename}`
        });
      } else {
        setAlertStatus({
          status: 'completed',
          completed_at: timestamp,
          message: `Re-downloaded ${downloadedFilename}`
        });
      }

      setToast({
        show: true,
        type: 'success',
        title: 'Re-download Completed',
        message: `${downloadedFilename} has been downloaded again`
      });
    } catch (error: any) {
      const errorMessage = error?.message || '';
      const isConnectionError = errorMessage.includes('Failed to fetch') || errorMessage.includes('ERR_CONNECTION_REFUSED');

      if (activeSection === 'download') {
        setDownloadStatus({
          status: 'idle',
          message: isConnectionError ? 'Server unavailable' : 'Re-download failed'
        });
      } else {
        setAlertStatus({
          status: 'idle',
          message: isConnectionError ? 'Server unavailable' : 'Re-download failed'
        });
      }

      setToast({
        show: true,
        type: 'error',
        title: 'Re-download Failed',
        message: isConnectionError
          ? 'Cannot connect to the server. Please ensure the backend server is running and API base URL is correct.'
          : error?.message || 'Failed to re-download file'
      });
    }
  };

  const handleDeleteHistory = useCallback(async (item: DownloadHistory) => {
    if (!canExecuteSimulationTabs) {
      setToast({
        show: true,
        type: 'error',
        title: 'Action Not Allowed',
        message: 'Only HO Admin can delete history records.'
      });
      return;
    }

    try {
      setToast({
        show: true,
        type: 'info',
        title: 'Deleting Record',
        message: 'Removing history entry and files...'
      });

      const response = await fetch(`${API_BASE}/api/v1/fulfillment/simulation-history/${item.id}`, {
        method: 'DELETE',
        headers: {
          ...getAuthAndTenantHeaders(site)
        }
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody?.message || `Failed to delete record (${response.status})`);
      }

      setToast({
        show: true,
        type: 'success',
        title: 'History Deleted',
        message: 'The history record and zip file have been deleted.'
      });

      fetchHistory();
    } catch (err: any) {
      console.error('Failed to delete history item:', err);
      setToast({
        show: true,
        type: 'error',
        title: 'Deletion Failed',
        message: err.message || 'Could not delete history record'
      });
    }
  }, [site, fetchHistory]);

  const handleOpenFolder = async (path?: string) => {
    if (!path) return;
    try {
      const response = await fetch(`${API_BASE}/api/v1/fulfillment/open-folder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthAndTenantHeaders(site)
        },
        body: JSON.stringify({ path })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to open folder');
      }

      setToast({
        show: true,
        type: 'success',
        title: 'Folder Opened',
        message: 'The folder has been opened in your file explorer'
      });
    } catch (error: any) {
      setToast({
        show: true,
        type: 'error',
        title: 'Open Failed',
        message: error?.message || 'Unable to open the folder'
      });
    }
  };

  const downloadTokenCost = tokenPricing.download_raw_data?.cost ?? 1;
  const alertsTokenCost = tokenPricing.generate_alerts?.cost ?? 1;
  const rtcTokenCost = tokenPricing.run_rtc_optimization?.cost ?? 2;
  const canRunDownloadByTokens = effectiveViewerRole === 'system_admin' || tokenBalance === null ? true : tokenBalance >= downloadTokenCost;
  const canRunAlertsByTokens = effectiveViewerRole === 'system_admin' || tokenBalance === null ? true : tokenBalance >= alertsTokenCost;
  const canRunRtcByTokens = effectiveViewerRole === 'system_admin' || tokenBalance === null ? true : tokenBalance >= rtcTokenCost;

  // Download Raw ZIP
  const executeDownload = async () => {
    if (!canExecuteSimulationTabs) {
      setToast({
        show: true,
        type: 'error',
        title: 'Execution Not Allowed',
        message: 'Only HO Admin can execute simulation tabs.'
      });
      return;
    }
    if (!canRunDownloadByTokens) {
      setToast({
        show: true,
        type: 'error',
        title: 'Insufficient Tokens',
        message: `Required: ${downloadTokenCost}, Available: ${tokenBalance ?? 0}`
      });
      return;
    }

    let progressInterval: any = null;
    try {
      let fromDate: string;
      let toDate: string;

      if (dateRangeMode === 'single') {
        if (!customDates.singleDate) {
          setToast({
            show: true,
            type: 'error',
            title: 'Date Required',
            message: 'Please select a date before downloading'
          });
          return;
        }
        fromDate = customDates.singleDate;
        toDate = customDates.singleDate;
      } else {
        if (!customDates.startDate || !customDates.endDate) {
          setToast({
            show: true,
            type: 'error',
            title: 'Date Range Required',
            message: 'Please select both start and end dates'
          });
          return;
        }
        fromDate = customDates.startDate;
        toDate = customDates.endDate;
      }

      const localInputPath = simulationInputs.localInputPath.trim();
      const outputPathsInput = simulationInputs.outputPaths
        .split(',')
        .map(path => path.trim())
        .filter(Boolean);
      const azureSasKey = simulationInputs.azureSasKey.trim();
      const cwd = simulationInputs.cwd.trim();

      if (!localInputPath || !azureSasKey || !cwd) {
        setToast({
          show: true,
          type: 'error',
          title: 'Configuration Required',
          message: 'Please fill Local Input Path, Azure SAS Key and cwd before download.'
        });
        return;
      }

      const outputPaths = outputPathsInput.length > 0 ? outputPathsInput : [localInputPath];

      const executionId = `sim_${Date.now()}`;
      setDownloadStatus({
        status: 'running',
        started_at: new Date().toISOString(),
        message: 'Running simulation and preparing download...',
        progress: 0
      });

      // Start polling progress
      progressInterval = setInterval(async () => {
        try {
          const res = await fetch(`${API_BASE}/api/v1/fulfillment/simulation-progress?execution_id=${executionId}`, {
            headers: getAuthAndTenantHeaders(site)
          });
          if (res.ok) {
            const data = await res.json();
            if (data.status === 'running') {
              setDownloadStatus(prev => ({
                ...prev,
                message: data.message || prev.message,
                progress: data.percent
              }));
            }
          }
        } catch (e) {
          // Ignore polling errors
        }
      }, 1000);

      const simulationPayload = {
        site_id: site || 'AXON001',
        local_input_output_path_map: {
          [localInputPath]: outputPaths
        },
        azure_data_lake_sas_key: azureSasKey,
        cwd,
        date_mode: dateRangeMode === 'single' ? 'single' : 'range',
        selected_date: dateRangeMode === 'single' ? fromDate : '',
        start_date: fromDate,
        end_date: toDate,
        input_location_with_date: 1,
        output_location_with_date: 1,
        input_datedash_format: 1,
        output_datedash_format: 1,
        input_reverse: 'True',
        output_reverse: 'False',
        upstream: 0,
        last_x_hours: 0,
        execution_id: executionId
      };

      const simulationResponse = await fetch(`${API_BASE}/api/v1/fulfillment/simulation-on-demand`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthAndTenantHeaders(site)
        },
        body: JSON.stringify(simulationPayload)
      });

      if (!simulationResponse.ok) {
        const errorBody = await simulationResponse.json().catch(() => ({}));
        throw new Error(errorBody?.message || `Simulation execution failed (${simulationResponse.status})`);
      }

      const simulationResult = await simulationResponse.json().catch(() => null);
      if (!simulationResult) {
        throw new Error('Invalid simulation response');
      }

      if (typeof simulationResult.tokens_remaining === 'number') {
        setTokenBalance(simulationResult.tokens_remaining);
      }

      // We no longer download a ZIP; backend does output transfer to path using original script logic.
      const generatedFiles: any[] = Array.isArray(simulationResult.generated_files)
        ? simulationResult.generated_files
        : [];

      const historyFilename = generatedFiles.length > 0
        ? generatedFiles[0].name
        : `simulation-${fromDate}-${toDate}`;

      const historyPath = generatedFiles.length > 0
        ? generatedFiles[0].path
        : '';

      const historySize = generatedFiles.length > 0
        ? generatedFiles[0].size
        : 'N/A';

      const historyCount = generatedFiles.length > 0
        ? generatedFiles[0].count
        : 0;

      const timestamp = new Date().toISOString();

      if (progressInterval) clearInterval(progressInterval);

      setDownloadStatus({
        status: 'completed',
        completed_at: timestamp,
        message: `Simulation complete: ${simulationResult.successful_commands || 0}/${simulationResult.total_commands || 0} commands executed.`,
        progress: 100
      });

      const newHistoryItem: DownloadHistory = {
        id: Date.now().toString(),
        filename: historyFilename,
        activityType: 'Download Raw Data',
        source: 'azure',
        folderName: historyFilename,
        path: historyPath,
        fromDate: fromDate,
        toDate: toDate,
        dateDisplay: fromDate === toDate ? fromDate : `${fromDate} to ${toDate}`,
        size: historySize,
        fileCount: historyCount,
        status: 'completed',
        timestamp,
        siteId: site || 'ACPL'
      };

      setDownloadHistory(prev => [newHistoryItem, ...prev].slice(0, 50));
      fetchHistory();

      setToast({
        show: true,
        type: 'success',
        title: 'Simulation Completed',
        message: `${generatedFiles.length} output file path(s) processed`
      });

    } catch (error: any) {
      // Only log non-connection errors
      const errorMessage = error?.message || '';
      if (!errorMessage.includes('Failed to fetch') && !errorMessage.includes('ERR_CONNECTION_REFUSED')) {
        console.error('Download error:', error);
      }

      const timestamp = new Date().toISOString();

      if (progressInterval) clearInterval(progressInterval);

      setDownloadStatus({
        status: 'idle', // Set to idle instead of failed for connection errors
        message: errorMessage.includes('Failed to fetch') || errorMessage.includes('ERR_CONNECTION_REFUSED')
          ? 'Server unavailable'
          : error?.message || 'Download failed'
      });

      setToast({
        show: true,
        type: 'error',
        title: 'Download Failed',
        message: errorMessage.includes('Failed to fetch') || errorMessage.includes('ERR_CONNECTION_REFUSED')
          ? 'Cannot connect to the server. Please ensure the backend server is running and API base URL is correct.'
          : error?.message || 'An error occurred while downloading the file'
      });
    }
  };

  // Web Scrape Download
  const executeWebScrape = async (
    plant: string,
    dateMode: string,
    manualDate: string,
    startDate: string,
    endDate: string
  ) => {
    if (!canExecuteSimulationTabs) {
      setToast({
        show: true,
        type: 'error',
        title: 'Execution Not Allowed',
        message: 'Only HO Admin can execute simulation tabs.'
      });
      return;
    }

    if (!plant) {
      setToast({
        show: true,
        type: 'error',
        title: 'Plant Required',
        message: 'Please select a plant before downloading'
      });
      return;
    }

    if (dateMode === 'manual' && !manualDate) {
      setToast({
        show: true,
        type: 'error',
        title: 'Date Required',
        message: 'Please select a specific date before downloading'
      });
      return;
    }

    if (dateMode === 'loop_dates' && (!startDate || !endDate)) {
      setToast({
        show: true,
        type: 'error',
        title: 'Date Range Required',
        message: 'Please select both start and end dates before downloading'
      });
      return;
    }

    setDownloadStatus({
      status: 'running',
      started_at: new Date().toISOString(),
      message: 'Scraping raw data from SCADA portal (might take up to 2-3 mins)...',
      progress: 5,
      isWebScrape: true
    });

    try {
      const response = await fetch(`${API_BASE}/api/v1/fulfillment/web-scrape-download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthAndTenantHeaders(site)
        },
        body: JSON.stringify({
          plant,
          date_mode: dateMode,
          manual_date: manualDate,
          start_date: startDate,
          end_date: endDate
        })
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody?.message || `Scraper execution failed (${response.status})`);
      }

      const resData = await response.json();

      if (resData.tokens_remaining !== undefined) {
        setTokenBalance(resData.tokens_remaining);
      }

      setDownloadStatus({
        status: 'running',
        started_at: new Date().toISOString(),
        message: 'Initializing SCADA browser automation...',
        progress: 5,
        executionId: resData.execution_id,
        activityId: resData.activity_id,
        isWebScrape: true
      });

      setToast({
        show: true,
        type: 'info',
        title: 'Scraper Started',
        message: 'SCADA Web Scraper launched in background.'
      });

    } catch (error: any) {
      console.error('Scraper error:', error);
      setDownloadStatus({
        status: 'idle',
        message: error.message || 'Scraper execution failed'
      });
      setToast({
        show: true,
        type: 'error',
        title: 'Scraping Failed',
        message: error.message || 'Failed to download scraped data'
      });
    }
  };

  // Download Alerts CSV
  const downloadAlerts = async () => {
    if (!canExecuteSimulationTabs) {
      setToast({
        show: true,
        type: 'error',
        title: 'Execution Not Allowed',
        message: 'Only HO Admin can execute simulation tabs.'
      });
      return;
    }
    if (!canRunAlertsByTokens) {
      setToast({
        show: true,
        type: 'error',
        title: 'Insufficient Tokens',
        message: `Required: ${alertsTokenCost}, Available: ${tokenBalance ?? 0}`
      });
      return;
    }

    try {
      const dateParam = customDates.singleDate || new Date().toISOString().slice(0, 10);

      setAlertStatus({
        status: 'running',
        started_at: new Date().toISOString(),
        message: 'Checking for alerts CSV...'
      });

      const headers: Record<string, string> = {};
      if (alertsEtag) headers['If-None-Match'] = alertsEtag;
      Object.assign(headers, getAuthAndTenantHeaders(site));

      const result = await safeFetchBlob(
        `${API_BASE}/api/v1/fulfillment/alerts-csv?date=${encodeURIComponent(dateParam)}`,
        { headers }
      );

      if (result === null) {
        // Server unavailable
        setAlertStatus({
          status: 'idle',
          message: 'Server unavailable. Please ensure the backend server is running.'
        });
        setToast({
          show: true,
          type: 'error',
          title: 'Server Unavailable',
          message: 'Cannot connect to the server. Please ensure the backend server is running and API base URL is correct.'
        });
        return;
      }

      // Handle special status codes
      if ('status' in result) {
        if (result.status === 304) {
          setAlertStatus({
            status: 'completed',
            completed_at: new Date().toISOString(),
            message: 'Alerts CSV unchanged (using cached version)'
          });

          setToast({
            show: true,
            type: 'info',
            title: 'No Changes',
            message: 'Alerts CSV unchanged, using cached version'
          });
          return;
        }

        if (result.status === 501) {
          const errorData = await result.response.json().catch(() => ({}));
          setAlertStatus({
            status: 'idle',
            message: 'Alerts CSV feature not yet implemented'
          });

          setToast({
            show: true,
            type: 'info',
            title: 'Feature Not Available',
            message: errorData.message || 'This feature is not yet implemented'
          });
          return;
        }

        if (result.status === 404) {
          setAlertStatus({
            status: 'idle',
            message: `No alerts CSV found for ${dateParam}`
          });

          setToast({
            show: true,
            type: 'error',
            title: 'File Not Found',
            message: `No alerts CSV found for ${dateParam}`
          });
          return;
        }

        const errorText = await result.response.text().catch(() => '');
        throw new Error(`HTTP ${result.status} ${result.response.statusText}: ${errorText}`);
      }

      // If we get here, result contains a valid blob and response
      const et = result.response.headers.get('ETag');
      if (et) setAlertsEtag(et);

      const filename = `alerts_${dateParam}.csv`;
      const tokenRemainingHeader = result.response.headers.get('X-Tokens-Remaining');
      if (tokenRemainingHeader && !Number.isNaN(Number(tokenRemainingHeader))) {
        setTokenBalance(Number(tokenRemainingHeader));
      }
      saveBlob(result.blob, filename);

      const fileSize = (result.blob.size / 1024).toFixed(2);
      const timestamp = new Date().toISOString();

      setAlertStatus({
        status: 'completed',
        completed_at: timestamp,
        message: `Successfully downloaded ${filename} (${fileSize} KB)`
      });

      // Add to alert history
      const newHistoryItem: DownloadHistory = {
        id: Date.now().toString(),
        filename: filename,
        fromDate: dateParam,
        toDate: dateParam,
        dateDisplay: dateParam,
        size: `${fileSize} KB`,
        status: 'completed',
        timestamp: timestamp,
        siteId: site || 'ACPL'
      };

      setAlertHistory(prev => [newHistoryItem, ...prev].slice(0, 50));

      setToast({
        show: true,
        type: 'success',
        title: 'CSV Downloaded',
        message: `${filename} (${fileSize} KB) has been downloaded successfully`
      });

    } catch (error: any) {
      const errorMessage = error?.message || '';
      const isConnectionError = errorMessage.includes('Failed to fetch') || errorMessage.includes('ERR_CONNECTION_REFUSED');

      setAlertStatus({
        status: 'idle',
        message: isConnectionError ? 'Server unavailable' : error?.message || 'Failed to download alerts CSV'
      });

      setToast({
        show: true,
        type: 'error',
        title: 'Download Failed',
        message: isConnectionError
          ? 'Cannot connect to the server. Please ensure the backend server is running and API base URL is correct.'
          : error?.message || 'Failed to download alerts CSV'
      });
    }
  };

  const handleRefresh = async () => {
    await checkDownloadStatus();
    await checkAlertStatus();
    // Restart polling by updating the key (triggers useEffect to restart)
    setPollingKey(prev => prev + 1);
  };

  // Polling - fast when a job is running, slow (15s) when idle
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let isMounted = true;

    const IDLE_INTERVAL = 15000;  // 15s when nothing is running
    const ACTIVE_INTERVAL = 2000; // 2s when a job is active
    const ERROR_INTERVAL = 30000; // 30s on error (server down etc.)

    const pollStatus = async () => {
      if (!isMounted) return;

      try {
        const downloadStatusRes = await checkDownloadStatus();
        const alertStatusRes = await checkAlertStatus();

        if (downloadStatusRes === 'unauthorized' || alertStatusRes === 'unauthorized') {
          // Missing/expired auth token: stop polling completely to avoid 401 noise.
          return;
        }

        if (downloadStatusRes === false || alertStatusRes === false) {
          // Server down, do not poll
          return;
        }

        const isRunning = (downloadStatusRes?.status === 'running' || alertStatusRes?.status === 'running');
        const nextInterval = isRunning ? ACTIVE_INTERVAL : IDLE_INTERVAL;

        if (isMounted) {
          timeoutId = setTimeout(pollStatus, nextInterval);
        }
      } catch {
        // Silently handle any errors - resume with slow backoff
        if (isMounted) {
          timeoutId = setTimeout(pollStatus, ERROR_INTERVAL);
        }
      }
    };

    // Initial delay before first poll
    timeoutId = setTimeout(() => {
      if (isMounted) {
        pollStatus();
      }
    }, 2000);

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [checkDownloadStatus, checkAlertStatus, pollingKey]); // Include pollingKey to restart on refresh


  if (!isRoleFilterLoading && !canAccessSimulationPage) {
    return (
      <div className="simulation-page min-h-screen bg-gray-50 pt-20 px-4">
        <div className="max-w-3xl mx-auto bg-white border border-red-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-black text-red-700">Access Restricted</h2>
          <p className="text-sm text-gray-700 mt-2 font-semibold">
            This page is only available for HO Admin. Site Admin and General User do not have access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="simulation-page min-h-screen np-page">

      <ToastNotification
        show={toast.show}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        progress={toast.progress}
        elapsedTime={toast.elapsedTime}
        persistent={toast.persistent}
        onClose={() => setToast({ ...toast, show: false })}
        duration={5000}
      />

      <div className="np-theme pt-16">
        <SimulationSidebar
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          downloadStatus={downloadStatus}
          alertStatus={alertStatus}
          rtcStatus={rtcStatus}
          config={sidebarConfig}
        />

        {/* Main content area - margin matches both sidebars */}
        <div
          className="transition-all duration-200"
          style={{ marginLeft: `calc(3.5rem + ${sidebarWidth}px)` }}
        >
          <div className="pb-8 -ml-6 mt-1.5">


            <div className="pt-1.5">
              {activeSection === 'wallet' ? (
                <TokenOperationsSection
                  viewerRole={viewerRole}
                  tokenBalance={tokenBalance}
                  tokenWalletLoading={tokenWalletLoading}
                  tokenPricing={tokenPricing}
                  requests={tokenRequests}
                  requestsLoading={tokenRequestsLoading}
                  requestAmount={requestAmount}
                  requestRemarks={requestRemarks}
                  onRequestAmountChange={setRequestAmount}
                  onRequestRemarksChange={setRequestRemarks}
                  onSubmitRequest={submitTokenRequest}
                  onRefreshRequests={fetchTokenRequests}
                  onApproveRequest={approveTokenRequest}
                  approveInProgressId={approveInProgressId}
                />
              ) : activeSection === 'download' ? (
                <DownloadSection
                  status={downloadStatus}
                  dateMode={dateRangeMode}
                  dates={customDates}
                  onDateModeChange={setDateRangeMode}
                  onDateChange={setCustomDates}
                  simulationInputs={simulationInputs}
                  onSimulationInputChange={updateSimulationInput}
                  onBrowseLocalInputPath={browseLocalInputFolder}
                  onBrowseOutputPath={browseOutputFolder}
                  onBrowseCwdPath={browseCwdFolder}
                  onExecute={executeDownload}
                  onExecuteWebScrape={executeWebScrape}
                  onStopExecute={handleStopExecution}
                  downloadHistory={downloadHistory}
                  onRedownload={handleRedownload}
                  onDeleteHistory={handleDeleteHistory}
                  onOpenFolder={handleOpenFolder}
                  actionsDisabled={!canExecuteSimulationTabs}
                  tokenCost={downloadTokenCost}
                  tokenBalance={tokenBalance}
                />
              ) : activeSection === 'alerts' ? (
                <AlertsSection
                  status={alertStatus}
                  singleDate={customDates.singleDate}
                  onDateChange={(newDate) => setCustomDates({ ...customDates, singleDate: newDate })}
                  onExecute={downloadAlerts}
                  alertHistory={alertHistory}
                  onRedownload={handleRedownload}
                  onDeleteHistory={handleDeleteHistory}
                  actionsDisabled={!canExecuteSimulationTabs}
                  tokenCost={alertsTokenCost}
                  tokenBalance={tokenBalance}
                />
              ) : (
                <RtcSimulationSection
                  siteCode={site}
                  onToast={setToast}
                  setStatus={setRtcStatus}
                  status={rtcStatus}
                  viewUserId={selectedUserId}
                  actionsDisabled={!canExecuteSimulationTabs}
                  tokenCost={rtcTokenCost}
                  tokenBalance={tokenBalance}
                  onTokenBalanceChange={setTokenBalance}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimulationOnDemand;


